/**
 * Per-field error surfacing for the write forms (Feature: granular-crud-api, task 13.3;
 * Requirement 11.5; Design: §11).
 *
 * The Soroush_API rejects a non-conforming write with `400 { error: 'validation_failed', errors }`,
 * where each {@link FieldError} carries a dotted `path` rooted at the entity — `check.spec.target`,
 * `alertRule.maxNotificationRate`, `check.spec.steps[0].url`. Req 11.5 requires each message to
 * appear *beside its field*, which needs exactly two things:
 *
 *  1. {@link FormField} — the one place a label, a control, and that control's errors are wired
 *     together. It owns the `id` / `aria-describedby` / `aria-invalid` plumbing, so a control is
 *     programmatically associated with its message rather than merely adjacent to it on screen.
 *  2. {@link unmappedFieldErrors} — the complement. A form declares the paths it renders; anything
 *     the API reports that no field claimed (a whole-entity defect like `check`, a path from a
 *     server-side rule the form has no input for) would otherwise vanish silently. The leftovers go
 *     to {@link FormErrorPanel}, so **every** returned error is displayed somewhere.
 *
 * A path may legitimately carry several errors (the validator reports each defect separately), so
 * everything here is list-valued rather than first-error-only.
 */

import type { ReactNode } from 'react';

import type { FieldError } from '@soroush/core';

/** Every error the API reported for exactly `path`. */
export function errorsForPath(
  errors: readonly FieldError[] | undefined,
  path: string,
): readonly FieldError[] {
  return errors === undefined ? [] : errors.filter((error) => error.path === path);
}

/**
 * The errors no rendered field claimed — the ones the form-level {@link FormErrorPanel} must show so
 * nothing the API said is dropped on the floor (Req 11.5).
 */
export function unmappedFieldErrors(
  errors: readonly FieldError[] | undefined,
  mappedPaths: readonly string[],
): readonly FieldError[] {
  if (errors === undefined) return [];
  const claimed = new Set(mappedPaths);
  return errors.filter((error) => !claimed.has(error.path));
}

/** The DOM id of a field's error text, referenced by the control's `aria-describedby`. */
export function fieldErrorId(path: string): string {
  return `field-error-${path.replace(/[^a-zA-Z0-9]+/g, '-')}`;
}

/**
 * The messages for one field, or nothing when the field is clean. Rendered inside the field's own
 * block and referenced by the control, so a screen reader announces the message with the input.
 */
export function FieldErrorText({
  errors,
  path,
}: {
  errors: readonly FieldError[] | undefined;
  path: string;
}): JSX.Element | null {
  const forField = errorsForPath(errors, path);
  if (forField.length === 0) return null;
  return (
    <p className="field-error" id={fieldErrorId(path)} data-field-error={path}>
      {forField.map((error) => error.message).join(' ')}
    </p>
  );
}

/**
 * A labelled control plus its per-field errors. The caller renders the control through the `render`
 * callback so it receives the generated `id` and the ARIA attributes that bind it to its message —
 * the association cannot be forgotten at a call site, because there is no way to render the control
 * without them.
 */
export function FormField({
  label,
  path,
  errors,
  hint,
  render,
}: {
  label: string;
  /** The API error path this field owns, e.g. `check.spec.target`. */
  path: string;
  errors: readonly FieldError[] | undefined;
  hint?: ReactNode;
  render: (props: {
    id: string;
    'aria-invalid'?: true;
    'aria-describedby'?: string;
  }) => ReactNode;
}): JSX.Element {
  const invalid = errorsForPath(errors, path).length > 0;
  const id = `field-${path.replace(/[^a-zA-Z0-9]+/g, '-')}`;
  return (
    <div className="form-field">
      <label htmlFor={id}>{label}</label>
      {render({
        id,
        ...(invalid ? { 'aria-invalid': true as const, 'aria-describedby': fieldErrorId(path) } : {}),
      })}
      {hint !== undefined ? <p className="field-hint muted">{hint}</p> : null}
      <FieldErrorText errors={errors} path={path} />
    </div>
  );
}

/**
 * The form-level error panel: the failed request's message (Req 11.6) plus every field error no
 * input claimed (Req 11.5). Renders nothing when the form has neither.
 */
export function FormErrorPanel({
  message,
  unmapped,
}: {
  message?: string;
  unmapped?: readonly FieldError[];
}): JSX.Element | null {
  const leftovers = unmapped ?? [];
  if (message === undefined && leftovers.length === 0) return null;
  return (
    <div role="alert" className="error-panel">
      {message !== undefined ? <p>{message}</p> : null}
      {leftovers.length > 0 ? (
        <ul className="form-error-list">
          {leftovers.map((error) => (
            <li key={`${error.path}:${error.message}`}>
              {error.path.length > 0 ? `${error.path}: ${error.message}` : error.message}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
